import * as React from "react";
import { EyeIcon, TrashIcon } from "lucide-react";
import { TableCell, TableRow } from "@/components/ui/table/table";
import { UserAvatar } from "@/components/ui/userAvatar";
import { Button } from "@/components/ui/button";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { Link } from "react-router";
import { useGetDirectoryUserQuery } from "@/api/users-admin";
import { BusinessReportsOutcomeListRowStatus } from "./outcomeListRowStatus";
import { BusinessReportOutcome, isBusinessReportOutcome } from "./businessReports.types";
import { CircleLoader } from "@/components/ui/circleLoader";
import { useDeleteBusinessReportOutcomeMutation } from "./businessReports.api";
import ConfirmModal from "@/views/drive/modals/confirmModal";
import colors from "tailwindcss/colors";
import { SnapshotCheckbox, type SnapshotCheckboxChangeMeta } from "@/components/snapshots/SnapshotCheckbox";

interface Props {
  isSelected?: boolean;
  onSelectSnapshot?: (checked: boolean, meta: SnapshotCheckboxChangeMeta) => void;
  onToggleSnapshot?: () => void;
  businessReportOutcome: BusinessReportOutcome;
}

export const BusinessReportsOutcomeListRow: React.FC<Props> = ({
  businessReportOutcome,
  isSelected = false,
  onSelectSnapshot,
  onToggleSnapshot,
}) => {
  const { data: createdBy } = useGetDirectoryUserQuery(businessReportOutcome.owner_id);
  const normalizedProgressbar = parseInt(String((businessReportOutcome.snapshot_progress || 0) * 100), 10);
  const [deleteBusinessReportOutcome] = useDeleteBusinessReportOutcomeMutation();
  const { formatDateShort, formatDayName, formatTime } = useDateFormatter();

  const isBusinessReport = isBusinessReportOutcome(businessReportOutcome);

  const handleDelete = async () => {
    try {
      await deleteBusinessReportOutcome({ id: businessReportOutcome.id });
    } catch (error) {
      console.error("Failed to delete outcome", error);
    }
  };

  return (
    <TableRow
      className={`hover:bg-gray-100/50 ${isSelected ? "bg-blue-100 font-semibold text-blue-900" : ""} cursor-pointer transition-colors duration-200`}
      onClick={onToggleSnapshot}
    >
      {onSelectSnapshot && (
        <TableCell className="w-[50px] pl-6">
          <SnapshotCheckbox
            checked={isSelected}
            onCheckedChange={onSelectSnapshot}
            ariaLabel={`Select snapshot ${businessReportOutcome.name}`}
            onClick={e => {
              // Prevent row click when clicking checkbox
              e.stopPropagation();
            }}
          />
        </TableCell>
      )}

      <TableCell className="whitespace-nowrap py-3 pl-6">
        <div className="flex flex-col">
          <span className="text-sm font-medium text-gray-900">{formatDateShort(businessReportOutcome.created_at)}</span>
          <div className="flex items-center gap-1 text-sm text-desert-sand-600">
            <span>
              {formatDayName(businessReportOutcome.created_at)} {formatTime(businessReportOutcome.created_at)}
            </span>
          </div>
        </div>
      </TableCell>

      <TableCell className="py-3">
        <div className="flex flex-col gap-1.5">
          <div
            className="line-clamp-1 text-sm font-medium text-gray-900"
            title={businessReportOutcome.business_report.name}
          >
            {businessReportOutcome.business_report.name}
          </div>
          <span
            className="line-clamp-1 text-sm text-gray-500"
            title={businessReportOutcome.fs_entry?.document_name || ""}
          >
            {businessReportOutcome.fs_entry?.document_name || ""}
          </span>
          <div className="flex items-center gap-2">
            <div className="flex" style={{ width: 200 }}>
              {businessReportOutcome.status === "IN_PROGRESS" && (
                <div className="mr-1">
                  <div className="flex items-center">
                    <CircleLoader fill={colors.gray[500]} />
                    <div className="text-xs">{normalizedProgressbar}%</div>
                  </div>
                </div>
              )}
              <BusinessReportsOutcomeListRowStatus
                status={businessReportOutcome.status}
                errorMessage={businessReportOutcome.error_message}
              />
            </div>
          </div>
        </div>
      </TableCell>

      <TableCell className="hidden py-3 lg:table-cell">
        <div className="flex min-w-[280px] items-center gap-3">
          <UserAvatar
            user={createdBy}
            identifier={businessReportOutcome.owner_id}
            size="lg"
            className="border-2 border-white shadow-sm"
          />
          <div className="flex flex-col items-start">
            <span className="line-clamp-1 rounded-md text-sm font-medium text-gray-900">
              {createdBy?.name} {createdBy?.surname}
            </span>
            <span className="line-clamp-1 text-xs text-gray-500">{createdBy?.email}</span>
          </div>
        </div>
      </TableCell>

      <TableCell className="whitespace-nowrap py-3 pr-6 text-right">
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/reports/outcome/${businessReportOutcome.id}`}
            title={isBusinessReport ? "View Report" : "View Snapshot"}
          >
            <Button
              variant="second-white"
              size="sm"
              className="flex items-center gap-2 px-3 py-2 text-xs"
              type="button"
            >
              <EyeIcon className="h-4 w-4 flex-shrink-0" />
              <span className="font-medium">{isBusinessReport ? "View Report" : "View Snapshot"}</span>
            </Button>
          </Link>
          <ConfirmModal
            triggerText=""
            title="Delete Outcome"
            description="Are you sure you want to delete this outcome? This action cannot be undone."
            onConfirm={handleDelete}
            icon={
              <Button
                variant="destructive"
                size="sm"
                className="flex items-center gap-2 px-3 py-2 text-xs"
                type="button"
                title="Delete Outcome"
              >
                <TrashIcon className="h-4 w-4 flex-shrink-0" />
              </Button>
            }
          />
        </div>
      </TableCell>
    </TableRow>
  );
};
